import { useState, useEffect } from 'react';
// Button import removed as it's not being used
import { Menu, X, Phone, Mail, ChevronDown } from 'lucide-react';

interface HeaderProps {
  currentPage: string;
  onPageChange: (page: string) => void;
}

export function Header({ currentPage, onPageChange }: HeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isProductsDropdownOpen, setIsProductsDropdownOpen] = useState(false);
  const [isMobileProductsOpen, setIsMobileProductsOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 50);
    };

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.products-dropdown-container')) {
        setIsProductsDropdownOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const navItems = [
    { id: 'home', label: 'Home' },
    { id: 'about', label: 'About Us' },
    { id: 'products', label: 'Products', hasDropdown: true },
    { id: 'services', label: 'Services' },
    { id: 'contact', label: 'Contact Us' },
  ];

  const productCategories = [
    { id: 'ferrous-materials', label: 'Ferrous Materials' },
    { id: 'non-ferrous-materials', label: 'Non-Ferrous Materials' },
    { id: 'rubber-materials', label: 'Rubber Materials' },
  ];

  const handleProductCategoryClick = (categoryId: string) => {
    onPageChange('products');
    setIsProductsDropdownOpen(false);
    setIsMenuOpen(false);
    // Scroll to section after a short delay to ensure page has loaded
    setTimeout(() => {
      const element = document.getElementById(categoryId);
      if (element) {
        const offset = 100; // Account for fixed header
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled 
        ? 'bg-white/75 backdrop-blur-md shadow-lg' 
        : 'bg-white shadow-lg'
    }`}>
      {/* Top Contact Bar */}
      <div className={`bg-gray-900 text-white py-2 transition-opacity duration-300 ${
        isScrolled ? 'opacity-0 h-0 overflow-hidden' : 'opacity-100'
      }`}>
        <div className="container mx-auto px-4 flex justify-between items-center text-sm">
          <div className="flex items-center space-x-4">
            <a href="tel:+971543643476" className="flex items-center space-x-1 hover:text-[#ffc533] transition-colors">
              <Phone className="w-4 h-4" />
              <span>+971 54 364 3476</span>
            </a>
            <a href="mailto:info@doubleclickfze.com" className="flex items-center space-x-1 hover:text-[#ffc533] transition-colors">
              <Mail className="w-4 h-4" />
              <span>info@doubleclickfze.com</span>
            </a>
          </div>
          <div className="hidden lg:block text-xs">
            <span>Office - C1 - 1F - SF0864, Ajman Free Zone C1 Building, Dubai, UAE | Established 2020</span>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="container mx-auto px-4">
        <div className={`flex justify-between items-center transition-all duration-300 ${
          isScrolled ? 'py-2' : 'py-4'
        }`}>
          {/* Logo */}
          <div className="flex items-center">
            <button 
              onClick={() => onPageChange('home')}
              className="logo-button cursor-pointer hover:opacity-80 transition-opacity duration-300 flex items-center gap-3"
            >
              <img 
                src="/assets/icons/logo.svg" 
                alt="Double Click Trading FZE Logo" 
                className={`w-auto transition-all duration-300 ${
                  isScrolled ? 'h-16' : 'h-20'
                }`}
              />
              <span className={`font-bold text-gray-900 transition-all duration-300 hidden md:block ${
                isScrolled ? 'text-base' : 'text-lg'
              }`}>
                DOUBLE CLICK TRADING FZE
              </span>
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              item.hasDropdown ? (
                <div 
                  key={item.id}
                  className="relative group products-dropdown-container"
                >
                  <button
                    onClick={() => {
                      onPageChange(item.id);
                      setIsProductsDropdownOpen(false);
                    }}
                    onMouseEnter={() => setIsProductsDropdownOpen(true)}
                    className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-1 ${
                      currentPage === item.id
                        ? 'bg-[#fe2b32] text-white'
                        : 'text-gray-700 hover:text-[#fe2b32] hover:bg-gray-50'
                    }`}
                  >
                    {item.label}
                    <ChevronDown className={`w-4 h-4 transition-transform ${isProductsDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {/* Dropdown Menu with bridge */}
                  {isProductsDropdownOpen && (
                    <div 
                      className="absolute top-full left-0 pt-2 z-50"
                      onMouseEnter={() => setIsProductsDropdownOpen(true)}
                      onMouseLeave={() => setIsProductsDropdownOpen(false)}
                    >
                      <div className="w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2">
                        {productCategories.map((category) => (
                          <button
                            key={category.id}
                            onClick={() => handleProductCategoryClick(category.id)}
                            className="w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-50 hover:text-[#fe2b32] transition-colors"
                          >
                            {category.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  key={item.id}
                  onClick={() => onPageChange(item.id)}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    currentPage === item.id
                      ? 'bg-[#fe2b32] text-white'
                      : 'text-gray-700 hover:text-[#fe2b32] hover:bg-gray-50'
                  }`}
                >
                  {item.label}
                </button>
              )
            ))}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden pb-4">
            <nav className="flex flex-col space-y-2">
              {navItems.map((item) => (
                item.hasDropdown ? (
                  <div key={item.id}>
                    <button
                      onClick={() => {
                        setIsMobileProductsOpen(!isMobileProductsOpen);
                        if (!isMobileProductsOpen) {
                          onPageChange(item.id);
                        }
                      }}
                      className={`w-full px-4 py-2 rounded-lg text-left transition-colors flex items-center justify-between ${
                        currentPage === item.id
                          ? 'bg-[#fe2b32] text-white'
                          : 'text-gray-700 hover:text-[#fe2b32] hover:bg-gray-50'
                      }`}
                    >
                      {item.label}
                      <ChevronDown className={`w-4 h-4 transition-transform ${isMobileProductsOpen ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {/* Mobile Dropdown */}
                    {isMobileProductsOpen && (
                      <div className="mt-2 ml-4 space-y-2">
                        {productCategories.map((category) => (
                          <button
                            key={category.id}
                            onClick={() => handleProductCategoryClick(category.id)}
                            className="w-full px-4 py-2 rounded-lg text-left text-sm text-gray-600 hover:bg-gray-50 hover:text-[#fe2b32] transition-colors"
                          >
                            {category.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    key={item.id}
                    onClick={() => {
                      onPageChange(item.id);
                      setIsMenuOpen(false);
                    }}
                    className={`px-4 py-2 rounded-lg text-left transition-colors ${
                      currentPage === item.id
                        ? 'bg-[#fe2b32] text-white'
                        : 'text-gray-700 hover:text-[#fe2b32] hover:bg-gray-50'
                    }`}
                  >
                    {item.label}
                  </button>
                )
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}